// Runtime configuration reader.
//
// Values flow: hugo.toml params → <meta name="site-params"> in head.html →
// this module → React components. That single path keeps per-environment
// config out of the JS bundle — one build runs against staging, production,
// or local dev by just changing HUGO_PARAMS_* at build time.

export interface SiteConfig {
  /** Public discovery API origin (/opportunities path prefix on api.stawi.org). */
  apiURL: string;
  /** Candidates / profile API origin (unified api.stawi.org root). */
  candidatesAPIURL: string;
  /** OIDC issuer (Ory Hydra). */
  oidcIssuer: string;
  /** Hydra client_id string for this environment. */
  oidcClientID: string;
  /** Partition XID from Thesa; passed as installation_id in auth URLs. */
  oidcInstallationID: string;
  /** Registered redirect URI — must match Hydra's client record exactly. */
  oidcRedirectURI: string;

  /** PostHog project API key (public, starts with "phc_"). Empty disables. */
  posthogApiKey: string;
  /** PostHog ingest host. Defaults to us.i.posthog.com when blank. */
  posthogHost: string;
}

const DEFAULTS: SiteConfig = {
  apiURL: 'https://api.stawi.org/opportunities',
  // Bare base URL — the matching service's /matching/* prefix is added
  // inline by each candidate-API call site (see api/candidates.ts and
  // Dashboard.tsx). Kept bare because the shared @stawi/profile widget
  // also uses this runtime and calls /profile.v1.*. See
  // api-gateway-path-prefix-convention memory for the org-wide rule.
  candidatesAPIURL: 'https://api.stawi.org',
  oidcIssuer: 'https://oauth2.stawi.org',
  // Production SPA client + partition. Keep in sync with ui/hugo.toml and
  // the tenancy greenfield seed (client d7is2kspf2t7cl19qlp0).
  oidcClientID: 'd7is2kspf2t7cl19qlp0',
  oidcInstallationID: 'd7gi6lkpf2t67dlsqreg',
  oidcRedirectURI:
    typeof window !== 'undefined'
      ? `${window.location.origin}/auth/callback/`
      : 'http://localhost:5170/auth/callback/',
  // PostHog — empty default so initPostHog() short-circuits unless
  // HUGO_PARAMS_posthogApiKey is set at build time. Keys are public
  // (phc_...) and safe in hugo.toml. posthogHost falls back to PostHog
  // Cloud US in the runtime module when blank.
  posthogApiKey: '',
  posthogHost: '',
};

let cached: SiteConfig | null = null;

/** Read the config once per page load and cache it. */
export function getConfig(): SiteConfig {
  if (cached) return cached;
  cached = loadFromMeta();
  return cached;
}

function loadFromMeta(): SiteConfig {
  if (typeof document === 'undefined') return DEFAULTS;
  const el = document.querySelector<HTMLMetaElement>('meta[name="site-params"]');
  if (!el) return DEFAULTS;
  try {
    const parsed = JSON.parse(el.content) as Partial<SiteConfig>;
    return { ...DEFAULTS, ...stripEmpty(parsed) };
  } catch {
    return DEFAULTS;
  }
}

function stripEmpty<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj) as [keyof T, unknown][]) {
    if (v !== '' && v != null) {
      out[k] = v as T[keyof T];
    }
  }
  return out;
}
